import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { oneTimesUpdate } from "@domain/coupons"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import { Stack } from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import {
  FlowMessageChannel,
  MutationOneTimesUpdateMutationArgs
} from "__generated__/graphql"
import { useState } from "react"
import * as yup from "yup"

type DublicateFlowDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

// Sentinel for "keep the source flow's channel": the backend skips channel filtering when no
// channelType is sent.
const SOURCE_CHANNEL = "SourceChannel"

type DublicateFlowFormData = {
  sourceFlowId: string
  targetCustomer: string
  channelType: FlowMessageChannel | typeof SOURCE_CHANNEL
}

const channelOptions = [
  { label: "WhatsApp Web", value: FlowMessageChannel.WhatsappWeb },
  { label: "WhatsApp Business", value: FlowMessageChannel.WhatsappBusiness },
  {
    label: "Meta WhatsApp Business",
    value: FlowMessageChannel.MetaWhatsappBusiness
  },
  { label: "Instagram", value: FlowMessageChannel.Instagram },
  { label: "Facebook", value: FlowMessageChannel.Facebook },
  { label: "Telegram", value: FlowMessageChannel.Telegram },
  { label: "Email", value: FlowMessageChannel.Email },
  { label: "Live Chat", value: FlowMessageChannel.LiveChat },
  { label: "Tiktok", value: FlowMessageChannel.Tiktok }
]

export default function DublicateFlowDialog({
  isOpen,
  setIsOpen
}: DublicateFlowDialogProps) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)

  const selectOptions = [
    {
      label: translate("duplicate_flow_source_channel"),
      value: SOURCE_CHANNEL
    },
    ...channelOptions
  ]

  const schema = yup
    .object({
      sourceFlowId: yup.string().required(),
      targetCustomer: yup.string().required(),
      channelType: yup
        .mixed<DublicateFlowFormData["channelType"]>()
        .oneOf([SOURCE_CHANNEL, ...Object.values(FlowMessageChannel)])
        .required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isValid }
  } = useControlledForm<DublicateFlowFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      channelType: SOURCE_CHANNEL
    }
  })

  const setClose = () => {
    setIsOpen(false)
  }

  const handleClose = () => {
    setIsOpen(false)
    reset()
  }

  const onSubmit = async (data: DublicateFlowFormData) => {
    setLoading(true)
    const {
      isSuccess,
      errorMessage,
      data: responseData
    } = await oneTimesUpdate({
      type: "DUBLICATE_FLOW" as MutationOneTimesUpdateMutationArgs["type"],
      metaData: {
        sourceFlowId: data.sourceFlowId,
        targetCustomer: data.targetCustomer,
        ...(data.channelType !== SOURCE_CHANNEL && {
          channelType: data.channelType
        })
      }
    })

    if (!isSuccess || !responseData) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    showSuccessToast(translate("succesfully_added"))
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title="Dublicate Flow"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("create"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid || loading
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<DublicateFlowFormData>
          label="Source Flow ID"
          disabled={loading}
          name="sourceFlowId"
          placeholder={translate("enter")}
          control={control}
          onChange={(e) => {
            setValue("sourceFlowId", e.target.value)
            trigger("sourceFlowId")
          }}
          required
        />
        <TextInput<DublicateFlowFormData>
          label="Target Customer ID"
          disabled={loading}
          name="targetCustomer"
          placeholder={translate("enter")}
          control={control}
          onChange={(e) => {
            setValue("targetCustomer", e.target.value)
            trigger("targetCustomer")
          }}
          required
        />
        <TextInput<DublicateFlowFormData>
          type="select"
          label="Channel Type"
          disabled={loading}
          name="channelType"
          placeholder={translate("select")}
          control={control}
          selectOptions={selectOptions}
          required
        />
      </Stack>
    </DialogProvider>
  )
}
